"use client";

import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/nextjs";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export class ClerkSignInErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, retryCount: 0 };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      "🚨 Clerk Sign-In Error Boundary caught an error:",
      error,
      errorInfo
    );

    // Check if this is a Clerk-related error
    const isClerkError = Boolean(
      error.message.includes("Object Not Found Matching Id") ||
        error.message.includes("ChunkLoadError") ||
        error.stack?.includes("clerk") ||
        error.stack?.includes("98150de33c239da6.js")
    );

    Sentry.captureException(error, {
      tags: {
        errorBoundary: "ClerkSignInErrorBoundary",
        isClerkError: isClerkError.toString(),
        component: "sign-in",
      },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      extra: {
        retryCount: this.state.retryCount,
        maxRetries: this.maxRetries,
        errorMessage: error.message,
        errorStack: error.stack,
      },
    });
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      console.log(
        `🔄 Retrying Clerk sign-in (attempt ${this.state.retryCount + 1}/${
          this.maxRetries
        })`
      );

      this.setState((prevState) => ({
        hasError: false,
        error: null,
        retryCount: prevState.retryCount + 1,
      }));

      // Force a small delay to let any pending Clerk operations complete
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      console.warn("🚫 Max retry attempts reached for Clerk sign-in");
      // Redirect to a safe page or show persistent error
      window.location.href = "/";
    }
  };

  handleReset = () => {
    // Clear any stored Clerk state and redirect to home
    console.log("🔄 Resetting Clerk state and redirecting to home");

    // Clear localStorage items that might contain stale Clerk data
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("clerk-") || key.includes("clerk")) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn("Could not clear localStorage:", e);
    }

    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI for sign-in failures
      return (
        this.props.fallback || (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="mb-4">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Sign-in temporarily unavailable
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  We&apos;re experiencing a temporary issue with the sign-in
                  service.
                  {this.state.retryCount < this.maxRetries
                    ? " Please try again in a moment."
                    : " Please visit the homepage and try again later."}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {this.state.retryCount < this.maxRetries ? (
                  <button
                    onClick={this.handleRetry}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                  >
                    Try Again ({this.maxRetries - this.state.retryCount}{" "}
                    attempts left)
                  </button>
                ) : (
                  <button
                    onClick={this.handleReset}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                  >
                    Go to Homepage
                  </button>
                )}

                <button
                  onClick={this.handleReset}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition-colors"
                >
                  Start Fresh
                </button>
              </div>

              <p className="text-xs text-gray-400 mt-4">
                Error ID: {this.state.error?.message?.substring(0, 8)}...
              </p>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
